import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { B, FONT } from '../lib/constants';

const SHEET_IDS = {
  1: '10XOOYS3yaXsmwf4wk5BHOXO1Y6LJ5c5qKMPzfAZShJ0',
  2: '1TeirSGjgSZalSDpyxxAHFImBZmmzCD7xeU99Hb4rAck',
  3: '12aZNIwtf4z8jveRGFyWRfHlqwvIe4lCGrPfj0RUHCcs',
  4: '1Q-GKQn4KMssGCRIg9o2AnFUT6CcyKQ5GVFd4-lWP-FE',
};

// Industry-standard construction loan draw weights (NAHB cost breakdown + typical 5-7 draw schedules).
// Sum of bucket targets = 100.
const INDUSTRY_BUCKETS = [
  { id: 'site_foundation',    target: 15, keywords: ['site work', 'lot clear', 'excavat', 'grading', 'footing', 'foundation', 'slab', 'concrete', 'backfill', 'pier'] },
  { id: 'framing',            target: 17, keywords: ['framing', 'truss', 'sheath', 'decking', 'subfloor', 'joist', 'beam', 'rough carpentry'] },
  { id: 'roofing',            target: 5,  keywords: ['roofing', 'shingle', 'roof tile', 'roof metal', 'roof underlay', 'roof dry'] },
  { id: 'exterior',           target: 8,  keywords: ['siding', 'brick', 'stone', 'stucco', 'window', 'exterior door', 'exterior paint', 'gutter', 'soffit', 'fascia', 'porch', 'masonry'] },
  { id: 'plumbing',           target: 7,  keywords: ['plumb', 'sewer', 'water line', 'water heater', 'gas line'] },
  { id: 'electrical',         target: 7,  keywords: ['electric', 'wiring', 'low voltage', 'data wir', 'media wir'] },
  { id: 'hvac',               target: 6,  keywords: ['hvac', 'ductwork', 'air handler', 'condens', 'mini split', 'furnace'] },
  { id: 'insulation_drywall', target: 8,  keywords: ['insul', 'drywall', 'sheetrock', 'tape', 'mud', 'texture'] },
  { id: 'cabinets_counters',  target: 6,  keywords: ['cabinet', 'countertop', 'vanity'] },
  { id: 'flooring_tile',      target: 7,  keywords: ['tile', 'flooring', 'hardwood', 'carpet', 'lvp', 'lvt', 'vinyl plank'] },
  { id: 'paint_trim',         target: 5,  keywords: ['paint', 'stain', 'trim', 'millwork', 'molding', 'baseboard', 'crown'] },
  { id: 'fixtures_appliances',target: 4,  keywords: ['fixture', 'appliance', 'mirror', 'hardware'] },
  { id: 'final',              target: 5,  keywords: ['punch', 'clean', 'landscap', 'driveway', 'walk through', 'closeout', 'inspect', 'certificate', 'occupancy', 'final'] },
];

function findBucket(phaseName) {
  const lower = (phaseName || '').toLowerCase();
  for (const bucket of INDUSTRY_BUCKETS) {
    for (const kw of bucket.keywords) {
      if (lower.includes(kw)) return bucket.id;
    }
  }
  return 'other';
}

function computeIndustryWeights(phaseNames) {
  const byBucket = {};
  phaseNames.forEach(name => {
    const bid = findBucket(name);
    (byBucket[bid] = byBucket[bid] || []).push(name);
  });
  const raw = {};
  phaseNames.forEach(name => {
    const bid = findBucket(name);
    if (bid === 'other') {
      raw[name] = 3 / (byBucket['other']?.length || 1);
    } else {
      const bucket = INDUSTRY_BUCKETS.find(b => b.id === bid);
      raw[name] = bucket.target / byBucket[bid].length;
    }
  });
  const sum = Object.values(raw).reduce((s, v) => s + v, 0);
  const result = {};
  phaseNames.forEach(name => {
    result[name] = sum > 0 ? (raw[name] / sum) * 100 : 0;
  });
  return result;
}

// Edit a single phase weight and proportionally rescale the others so total = 100
function editWeight(weights, editedName, newWeight) {
  newWeight = Math.max(0, Math.min(100, newWeight));
  const others = Object.entries(weights).filter(([n]) => n !== editedName);
  const otherSum = others.reduce((s, [_, w]) => s + w, 0);
  const target = 100 - newWeight;
  const result = { [editedName]: newWeight };
  if (others.length === 0) return result;
  if (otherSum === 0) {
    const eq = target / others.length;
    others.forEach(([n]) => { result[n] = eq; });
  } else {
    const scale = target / otherSum;
    others.forEach(([n, w]) => { result[n] = w * scale; });
  }
  return result;
}

function normalize(weights) {
  const sum = Object.values(weights).reduce((s, v) => s + v, 0);
  if (sum === 0) return weights;
  const result = {};
  Object.keys(weights).forEach(k => { result[k] = (weights[k] / sum) * 100; });
  return result;
}

export default function PhaseTab({ project, onRefresh }) {
  const [phaseOrder, setPhaseOrder] = useState([]);          // ordered list of names from sheet
  const [weights, setWeights] = useState({});                // { name: weight }
  const [completedPhases, setCompletedPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [editing, setEditing] = useState(null);              // { name, value } or null

  useEffect(() => { loadData(); }, [project.id]);

  const loadData = async () => {
    setLoading(true);
    const { data: proj } = await supabase
      .from('projects').select('completed_phases').eq('id', project.id).single();
    const completed = proj?.completed_phases || [];
    setCompletedPhases(completed);

    // Cached phase order (supports old [string,...] and new [{name,weight},...] formats)
    let order = [];
    const cachedOrder = localStorage.getItem(`phases_${project.id}`);
    if (cachedOrder) {
      try {
        const parsed = JSON.parse(cachedOrder);
        order = parsed.map(p => typeof p === 'string' ? p : p?.name).filter(Boolean);
      } catch(e) {}
    }

    // Cached custom weights map
    let w = {};
    const cachedWeights = localStorage.getItem(`phase_weights_${project.id}`);
    if (cachedWeights) {
      try { w = JSON.parse(cachedWeights) || {}; } catch(e) {}
    }

    // If we have phases but no weights cached, fill in industry defaults
    if (order.length > 0 && Object.keys(w).length === 0) {
      w = computeIndustryWeights(order);
      localStorage.setItem(`phase_weights_${project.id}`, JSON.stringify(w));
    }

    setPhaseOrder(order);
    setWeights(w);
    setLoading(false);
    syncFromSheet(completed, w);
  };

  const syncFromSheet = async (currentCompleted, currentWeights) => {
    const token = localStorage.getItem('google_access_token');
    const sheetId = SHEET_IDS[parseInt(project.id)];
    if (!token || !sheetId) return;
    setSyncing(true);
    try {
      const res = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/Gantt%20Chart!A:Z?majorDimension=ROWS`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const rows = data.values || [];

      // Detect header row + description column
      let headerRow = -1, descCol = 3;
      for (let i = 0; i < Math.min(20, rows.length); i++) {
        const row = rows[i] || [];
        const joined = row.join(' ').toLowerCase();
        if (joined.includes('description') || joined.includes('phase') || joined.includes('task')) {
          headerRow = i;
          row.forEach((c, idx) => {
            const cell = (c || '').toLowerCase().trim();
            if (cell === 'description' || cell === 'task' || cell === 'phase') descCol = idx;
          });
          break;
        }
      }

      const dataRows = headerRow >= 0 ? rows.slice(headerRow + 1) : rows;
      const seen = new Set();
      const names = [];
      for (const r of dataRows) {
        const d = r[descCol]?.trim();
        if (!d || d.length < 2) continue;
        const lower = d.toLowerCase();
        if (lower.includes('description') || lower.includes('total')) continue;
        if (seen.has(d)) continue;
        seen.add(d);
        names.push(d);
      }

      if (names.length === 0) { setSyncing(false); return; }

      // Build new weights: keep existing values for known phases, industry default for new ones, then normalize to 100
      const industryMap = computeIndustryWeights(names);
      let newWeights = {};
      names.forEach(name => {
        if (currentWeights[name] !== undefined && currentWeights[name] !== null) {
          newWeights[name] = currentWeights[name];
        } else {
          newWeights[name] = industryMap[name] || 0;
        }
      });
      newWeights = normalize(newWeights);

      setPhaseOrder(names);
      setWeights(newWeights);
      localStorage.setItem(`phases_${project.id}`, JSON.stringify(names));
      localStorage.setItem(`phase_weights_${project.id}`, JSON.stringify(newWeights));
      setLastSync(new Date().toLocaleTimeString());
      await writeProgress(newWeights, currentCompleted);
    } catch(e) { console.error('Phase sync error:', e); }
    setSyncing(false);
  };

  const writeProgress = async (weightsMap, completed) => {
    if (!weightsMap || Object.keys(weightsMap).length === 0) return;
    const totalAll = Object.values(weightsMap).reduce((s, w) => s + w, 0);
    if (totalAll < 50) return; // safety: don't write if weights aren't valid
    const total = Object.entries(weightsMap)
      .filter(([n]) => completed.includes(n))
      .reduce((s, [_, w]) => s + w, 0);
    const progress = Math.min(100, Math.round(total));
    await supabase.from('projects').update({ progress }).eq('id', project.id);
    if (onRefresh) onRefresh();
  };

  const handleCompletePhase = async (phaseName) => {
    if (completedPhases.includes(phaseName)) return;
    setCompleting(phaseName);
    const newCompleted = [...completedPhases, phaseName];
    await supabase.from('projects').update({ completed_phases: newCompleted }).eq('id', project.id);
    setCompletedPhases(newCompleted);
    await writeProgress(weights, newCompleted);

    const { data: templates } = await supabase
      .from('decision_templates').select('*').eq('send_on_phase', phaseName);

    if (templates && templates.length > 0) {
      const decisions = templates.map(t => ({
        project_id: parseInt(project.id),
        category: t.category,
        item: t.item,
        notes: t.notes || null,
        answer_type: t.answer_type || 'text',
        options: t.options || [],
        status: 'pending',
      }));
      await supabase.from('decisions').insert(decisions);
      try {
        const { notifyProjectClients } = await import('../hooks/usePushNotifications');
        await notifyProjectClients(
          project.id,
          `${project.name} — Decisions Ready`,
          `${decisions.length} new decision${decisions.length !== 1 ? 's' : ''} ready for your input`,
          `${window.location.origin}?project=${project.id}&tab=decisions`
        );
      } catch(e) {}
    }
    setCompleting(null);
  };

  const handleUncomplete = async (phaseName) => {
    const newCompleted = completedPhases.filter(p => p !== phaseName);
    await supabase.from('projects').update({ completed_phases: newCompleted }).eq('id', project.id);
    setCompletedPhases(newCompleted);
    await writeProgress(weights, newCompleted);
  };

  const startEdit = (name, currentWeight) => {
    setEditing({ name, value: (currentWeight || 0).toFixed(1).replace(/\.0$/, '') });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const newVal = parseFloat(editing.value);
    if (isNaN(newVal)) { setEditing(null); return; }
    const newWeights = editWeight(weights, editing.name, newVal);
    setWeights(newWeights);
    localStorage.setItem(`phase_weights_${project.id}`, JSON.stringify(newWeights));
    setEditing(null);
    await writeProgress(newWeights, completedPhases);
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Reset all phase weights to construction-loan industry standards?')) return;
    const newWeights = computeIndustryWeights(phaseOrder);
    setWeights(newWeights);
    localStorage.setItem(`phase_weights_${project.id}`, JSON.stringify(newWeights));
    await writeProgress(newWeights, completedPhases);
  };

  const completedCount = phaseOrder.filter(n => completedPhases.includes(n)).length;
  const pct = Math.min(100, Math.round(
    phaseOrder.filter(n => completedPhases.includes(n)).reduce((s, n) => s + (weights[n] || 0), 0)
  ));

  const fmtWeight = (w) => {
    if (!w || w <= 0) return '0%';
    const rounded = w.toFixed(1).replace(/\.0$/, '');
    return `${rounded}%`;
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Build Phases</div>
          <div style={{ color: B.muted, fontSize: 12, marginTop: 2 }}>
            {completedCount} of {phaseOrder.length} complete
            {lastSync && <span style={{ marginLeft: 8 }}>· Synced {lastSync}</span>}
          </div>
        </div>
        <button onClick={() => syncFromSheet(completedPhases, weights)} disabled={syncing}
          style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: syncing ? B.muted : B.goldlt, fontSize: 12, fontFamily: FONT }}>
          {syncing ? 'Syncing…' : '↻ Sync'}
        </button>
      </div>

      {phaseOrder.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ color: B.muted, fontSize: 11 }}>Project Progress</div>
            <div style={{ color: B.goldlt, fontSize: 11, fontWeight: 700 }}>{pct}%</div>
          </div>
          <div style={{ background: B.slate, borderRadius: 999, height: 6 }}>
            <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${B.goldlt},${B.gold})`, borderRadius: 999, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: B.muted, fontSize: 13, textAlign: 'center', padding: 32 }}>Loading phases…</div>
      ) : phaseOrder.length === 0 ? (
        <div style={{ background: B.navy, borderRadius: 14, padding: 32, textAlign: 'center', border: `1px solid ${B.slate}` }}>
          <div style={{ color: B.muted, fontSize: 13, marginBottom: 8 }}>No phases found.</div>
          <div style={{ color: B.muted, fontSize: 11 }}>Make sure Google is connected and tap ↻ Sync.</div>
        </div>
      ) : phaseOrder.map((name, i) => {
        const isDone = completedPhases.includes(name);
        const isCompleting = completing === name;
        const weight = weights[name] || 0;
        const isEditing = editing?.name === name;
        return (
          <div key={i} style={{ background: B.navy, borderRadius: 12, padding: '14px 16px', marginBottom: 8, border: `1px solid ${isDone ? '#4CAF7D' : B.slate}`, borderLeft: `3px solid ${isDone ? '#4CAF7D' : B.gold}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button
                onClick={() => isDone ? handleUncomplete(name) : handleCompletePhase(name)}
                disabled={isCompleting}
                style={{ width: 24, height: 24, borderRadius: 6, border: `2px solid ${isDone ? '#4CAF7D' : B.gold}`, background: isDone ? '#4CAF7D' : 'transparent', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                {isCompleting ? <span style={{ fontSize: 10, color: B.muted }}>…</span> : isDone ? <span style={{ color: '#fff', fontSize: 14, fontWeight: 800 }}>✓</span> : null}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: isDone ? '#4CAF7D' : B.white, fontSize: 14, fontWeight: 600, textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.8 : 1 }}>
                  {name}
                </div>
                {isEditing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <input
                      type="number"
                      autoFocus
                      value={editing.value}
                      onChange={e => setEditing({ ...editing, value: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                      onBlur={saveEdit}
                      min="0" max="100" step="0.5"
                      style={{ width: 70, background: B.mid, border: `1px solid ${B.gold}`, borderRadius: 6, padding: '4px 8px', color: B.goldlt, fontSize: 13, fontWeight: 700, outline: 'none', fontFamily: FONT }}
                    />
                    <span style={{ color: B.goldlt, fontSize: 13, fontWeight: 700 }}>%</span>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); startEdit(name, weight); }}
                    style={{ background: 'transparent', border: 'none', color: B.goldlt, fontSize: 11, fontWeight: 700, marginTop: 3, padding: 0, cursor: 'pointer', fontFamily: FONT, letterSpacing: 0.5, textAlign: 'left' }}>
                    {fmtWeight(weight)} <span style={{ color: B.muted, fontSize: 9, fontWeight: 400 }}>· tap to edit</span>
                  </button>
                )}
              </div>
              {isDone && (
                <div style={{ background: 'rgba(76,175,61,0.12)', color: '#4CAF7D', fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '3px 8px', flexShrink: 0 }}>
                  Complete
                </div>
              )}
            </div>
          </div>
        );
      })}

      {phaseOrder.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <button onClick={resetToDefaults}
            style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '8px 16px', cursor: 'pointer', color: B.muted, fontSize: 11, fontFamily: FONT }}>
            Reset to industry defaults
          </button>
        </div>
      )}

      {phaseOrder.length > 0 && (
        <div style={{ color: B.muted, fontSize: 11, textAlign: 'center', marginTop: 14, lineHeight: 1.6, padding: '0 12px' }}>
          Weights default to construction-loan industry standards. Tap any weight to customize — the others auto-adjust to keep the total at 100%.
        </div>
      )}
    </div>
  );
}
